import { useLayoutEffect, useRef, useState } from "react";

import {
  shouldUseCompactComposerFooter,
  shouldUseCompactComposerPrimaryActions,
} from "../composerFooterLayout";

interface UseComposerFooterCompactnessInput {
  /** The Composer's own form element. Compact footer is its width, not the viewport's. */
  readonly composerFormRef: React.RefObject<HTMLFormElement | null>;
  readonly hasWideActions: boolean;
  /**
   * Changes whenever the footer swaps which actions it renders. The observer
   * cannot see that on its own, because a control swap that keeps the form the
   * same width fires no resize entry, and the new controls may need the wider
   * threshold.
   */
  readonly actionLayoutKey: string;
  /** Re-measure on thread change: a different thread arrives with different chrome. */
  readonly activeThreadId: string | null;
  /** Both absent on the landing, which has no timeline to stick to. */
  readonly shouldAutoScrollRef?: React.MutableRefObject<boolean> | undefined;
  readonly scheduleStickToBottom?: (() => void) | undefined;
}

interface ComposerFooterCompactness {
  readonly footerCompact: boolean;
  readonly primaryActionsCompact: boolean;
}

/**
 * Measure the Composer form and decide the Compact footer from its width.
 *
 * This lives outside ChatComposer because the measurement owns the compactness
 * state and a ResizeObserver whose cleanup has to survive every unrelated
 * render of an orchestrator already over the file-size cap. The height branch
 * is here for the same reason: the observer is already watching the form, and a
 * second observer for the same box would report the two facts in an order
 * nothing controls.
 *
 * The height change re-sticks the timeline to the bottom. Without it, a footer
 * that grows by a row pushes the newest turn off the bottom of the thread while
 * a person is reading it. The landing has no timeline and leaves that pair of
 * inputs out.
 *
 * The width is the only input to the fold. Resting, focus and scrolling never
 * reach it.
 */
export function useComposerFooterCompactness({
  composerFormRef,
  hasWideActions,
  actionLayoutKey,
  activeThreadId,
  shouldAutoScrollRef,
  scheduleStickToBottom,
}: UseComposerFooterCompactnessInput): ComposerFooterCompactness {
  const [compactness, setCompactness] = useState<ComposerFooterCompactness>({
    footerCompact: false,
    primaryActionsCompact: false,
  });
  const composerFormHeightRef = useRef(0);

  useLayoutEffect(() => {
    const composerForm = composerFormRef.current;
    if (!composerForm) return;
    // The decision reads the state it is replacing, so it goes through the
    // updater rather than a captured value: the effect runs once per thread
    // and every resize after that would otherwise measure against the
    // compactness this closure was born with.
    const applyFooterCompactness = () => {
      const composerFormWidth = composerForm.clientWidth;
      setCompactness((previous) => {
        const footerCompact = shouldUseCompactComposerFooter(composerFormWidth, {
          hasWideActions,
          previous: previous.footerCompact,
        });
        const primaryActionsCompact =
          footerCompact &&
          shouldUseCompactComposerPrimaryActions(composerFormWidth, {
            hasWideActions,
            previous: previous.primaryActionsCompact,
          });
        return previous.footerCompact === footerCompact &&
          previous.primaryActionsCompact === primaryActionsCompact
          ? previous
          : { footerCompact, primaryActionsCompact };
      });
    };

    composerFormHeightRef.current = composerForm.getBoundingClientRect().height;
    applyFooterCompactness();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;
      applyFooterCompactness();
      const nextHeight = entry.contentRect.height;
      const previousHeight = composerFormHeightRef.current;
      composerFormHeightRef.current = nextHeight;
      if (previousHeight > 0 && Math.abs(nextHeight - previousHeight) < 0.5) return;
      if (!shouldAutoScrollRef?.current) return;
      scheduleStickToBottom?.();
    });

    observer.observe(composerForm);
    return () => {
      observer.disconnect();
    };
  }, [
    activeThreadId,
    actionLayoutKey,
    composerFormRef,
    hasWideActions,
    scheduleStickToBottom,
    shouldAutoScrollRef,
  ]);

  return compactness;
}
